import { useRef, useState } from "react";
import { Bold, Eye, Heading, Italic, Link2, List, ListOrdered, Pencil } from "lucide-react";
import { FormattedText } from "@sga/shared/form-renderer";

/**
 * The editor for staff-authored copy: terms and conditions bodies, rich-text blocks, privacy
 * notices.
 *
 * A plain textarea shipped a fifteen-clause terms document to applicants as one unbroken
 * paragraph, because nothing in the pipeline distinguished a line break from a space. This gives a
 * coordinator the four things such a document actually needs — numbered clauses, bullets, headings
 * and bold — through the small markup subset `packages/shared/src/form-renderer/markup.ts` parses.
 *
 * The toolbar exists so that nobody has to know that subset. The preview exists so that nobody has
 * to publish to find out what it did.
 */

const TOOLBAR_BUTTON =
  "flex h-7 w-7 items-center justify-center rounded border text-muted-foreground hover:bg-secondary hover:text-foreground";

export const MarkupEditor = ({
  id,
  value,
  onChange,
  rows = 10,
  placeholder,
  livePreview = false,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  rows?: number;
  placeholder?: string;
  /**
   * Shows the formatted result beside the text as it is typed, instead of behind the preview
   * toggle.
   *
   * Worth the space wherever the *look* of the message is the point rather than its wording — an
   * event invitation, a broadcast. Without it the editor reads as a plain textarea that happens to
   * accept asterisks, and people reasonably conclude the formatting is not working.
   */
  livePreview?: boolean;
}): JSX.Element => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [previewing, setPreviewing] = useState(false);
  const showLive = livePreview;

  /** Re-selects the text the button just acted on, so typing replaces the placeholder word. */
  const reselect = (start: number, length: number): void => {
    requestAnimationFrame(() => {
      const element = ref.current;
      if (!element) return;
      element.focus();
      element.setSelectionRange(start, start + length);
    });
  };

  const surround = (before: string, after: string, fallback: string): void => {
    const element = ref.current;
    if (!element) return;
    const { selectionStart, selectionEnd } = element;
    const selected = value.slice(selectionStart, selectionEnd) || fallback;
    onChange(
      value.slice(0, selectionStart) + before + selected + after + value.slice(selectionEnd),
    );
    reselect(selectionStart + before.length, selected.length);
  };

  /** Prefixes every line the selection touches — a list button must work on a whole block. */
  const prefixLines = (prefix: (index: number) => string, fallback: string): void => {
    const element = ref.current;
    if (!element) return;
    const { selectionStart, selectionEnd } = element;
    const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
    const afterEnd = value.indexOf("\n", selectionEnd);
    const lineEnd = afterEnd === -1 ? value.length : afterEnd;

    const block = value.slice(lineStart, lineEnd) || fallback;
    const prefixed = block
      .split("\n")
      .map((line, index) => `${prefix(index)}${line}`)
      .join("\n");

    onChange(value.slice(0, lineStart) + prefixed + value.slice(lineEnd));
    reselect(lineStart, prefixed.length);
  };

  return (
    <div className="rounded-md border">
      <div className="flex flex-wrap items-center gap-1 border-b p-1">
        <button
          type="button"
          className={TOOLBAR_BUTTON}
          title="Bold"
          aria-label="Bold"
          onClick={() => surround("**", "**", "bold text")}
        >
          <Bold aria-hidden className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={TOOLBAR_BUTTON}
          title="Italic"
          aria-label="Italic"
          onClick={() => surround("*", "*", "italic text")}
        >
          <Italic aria-hidden className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={TOOLBAR_BUTTON}
          title="Heading"
          aria-label="Heading"
          onClick={() => prefixLines(() => "## ", "Heading")}
        >
          <Heading aria-hidden className="h-3.5 w-3.5" />
        </button>

        <span aria-hidden className="mx-1 h-4 w-px bg-border" />

        <button
          type="button"
          className={TOOLBAR_BUTTON}
          title="Numbered list"
          aria-label="Numbered list"
          onClick={() => prefixLines((index) => `${index + 1}. `, "First clause")}
        >
          <ListOrdered aria-hidden className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={TOOLBAR_BUTTON}
          title="Bullet list"
          aria-label="Bullet list"
          onClick={() => prefixLines(() => "- ", "First point")}
        >
          <List aria-hidden className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={TOOLBAR_BUTTON}
          title="Link"
          aria-label="Link"
          onClick={() => surround("[", "](https://)", "link text")}
        >
          <Link2 aria-hidden className="h-3.5 w-3.5" />
        </button>

        <button
          type="button"
          onClick={() => setPreviewing((current) => !current)}
          aria-pressed={previewing}
          hidden={showLive}
          className="ml-auto flex items-center gap-1 rounded border px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          {previewing ? (
            <>
              <Pencil aria-hidden className="h-3 w-3" /> Edit
            </>
          ) : (
            <>
              <Eye aria-hidden className="h-3 w-3" /> Preview
            </>
          )}
        </button>
      </div>

      {showLive ? (
        // Side by side on a wide screen, stacked on a narrow one. The preview is the same
        // `FormattedText` the applicant-facing renderer uses, so what is shown here is what the
        // reader gets — not an approximation of it.
        <div className="grid divide-y md:grid-cols-2 md:divide-x md:divide-y-0">
          <textarea
            id={id}
            ref={ref}
            rows={rows}
            value={value}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
            className="w-full resize-y bg-transparent px-2 py-1.5 font-mono text-xs outline-none"
          />
          <div className="min-h-[8rem] overflow-y-auto p-3 text-xs" aria-live="polite">
            {value.trim() ? (
              <FormattedText text={value} />
            ) : (
              <p className="text-muted-foreground">
                Formatted text appears here as you type. Use the buttons above, or type{" "}
                <code>**bold**</code>.
              </p>
            )}
          </div>
        </div>
      ) : previewing ? (
        <div className="min-h-[8rem] p-3 text-xs">
          {value.trim() ? (
            <FormattedText text={value} />
          ) : (
            <p className="text-muted-foreground">Nothing to preview yet.</p>
          )}
        </div>
      ) : (
        <textarea
          id={id}
          ref={ref}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="w-full resize-y bg-transparent px-2 py-1.5 font-mono text-xs outline-none"
        />
      )}

      <p className="border-t px-2 py-1 text-[10px] text-muted-foreground">
        Use the buttons, or type <code>**bold**</code>, <code>1.</code> for numbered clauses and{" "}
        <code>-</code> for bullets. A blank line starts a new paragraph.
      </p>
    </div>
  );
};
